import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from './state/app.state';
import { Observable, filter, take } from 'rxjs';
import {
  selectUploadCSV,
  selectUploadCSVFailure,
  selectUploadEmails,
  selectUploadEmailsFailure
} from './state/dashboard/dashboard.selectors';
import { uploadCSV, uploadEmails } from './state/dashboard/dashboard.actions';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  constructor(private store: Store<AppState>) {}
  MAX_FILE_SIZE_MB: number = 10;

  /**
   * Handles CSV upload process with batch type
   * @param file File to upload
   * @param batchType Type of data in the file ('email' or 'linkedin')
   * @returns Promise with the result of the upload
   */
  handleCSVUpload(file: File, batchType: string = 'email'): Promise<any> {
    return this.validateCSV(file, batchType)
      .then((validationResult) => {
        if (!validationResult.isValid) {
          throw new Error('Invalid file'); // Return object on validation failure
        }

        // Store the count for use later
        const count = validationResult.validCount;

        return this.uploadCSV(file, batchType)
          .then(response => {
            // Enrich the response with our local count if API didn't provide one
            if (batchType === 'email' &&
              (!response.processing_result ||
                (!response.processing_result.total_emails && !response.processing_result.total_urls))) {

              // Create or update processing_result with our count
              if (!response.processing_result) {
                response.processing_result = {};
              }

              // Add the count as total_emails
              response.processing_result.total_emails = count;
            }

            return response;
          });
      })
      .then(
        (uploadResponse) => {
          return {
            success: uploadResponse.credit_validation && uploadResponse.credit_validation.success || true,
            data: uploadResponse,
          };
        }
      )
      .catch((error) => {
        return { success: false, message: error.message }; // Return error object
      });
  }

  /**
   * Handles pasted email upload
   * @param emails Array of email addresses
   * @returns Promise with the result of the upload
   */
  handleEmailsUpload(emails: string[]): Promise<any> {
    console.log('[FileUploadService] handleEmailsUpload called with', emails.length, 'emails');

    return this.validateEmails(emails)
      .then((isValid) => {
        if (!isValid) {
          throw new Error('Invalid emails'); // Return object on validation failure
        }

        if (emails.length === 0) {
          throw new Error('No emails to upload'); // Catch empty array
        }

        console.log('[FileUploadService] Emails validated successfully, proceeding to upload');
        return this.uploadEmails(emails); // Proceed with upload and return the object
      })
      .then(
        (uploadResponse) => {
          console.log('[FileUploadService] Upload response received:', uploadResponse);
          // Extract success status and any relevant data from the response
          return {
            success: true, // Force success if we get a response
            data: uploadResponse,
            count: emails.length // Include the count of emails in the response
          };
        }
      )
      .catch((error) => {
        console.error('[FileUploadService] Email upload error:', error);
        return {
          success: false,
          message: error.message || 'An unexpected error occurred during email upload'
        };
      });
  }

  /**
   * Validates all emails in array
   * @param emails Array of email addresses
   * @returns Promise<boolean> indicating if all emails are valid
   */
  validateEmails(emails: string[]): Promise<boolean> {
    return new Promise((resolve) => {
      if (!emails || emails.length === 0) {
        resolve(false);
        return;
      }

      // Check if any email is invalid
      const invalidEmails = emails.filter(email => !this.isValidEmail(email));

      if (invalidEmails.length > 0) {
        resolve(false);
        return;
      }

      resolve(true);
    });
  }

  /**
   * Validates the CSV file based on batch type
   * @param file File to validate
   * @param batchType Type of data ('email' or 'linkedin')
   * @returns Promise<{isValid: boolean, validCount: number}> with validation result and count
   */
  validateCSV(file: File, batchType: string = 'email'): Promise<{isValid: boolean, validCount: number}> {
    return new Promise((resolve, reject) => {
      if (file.size > this.MAX_FILE_SIZE_MB * 1024 * 1024) {
        resolve({isValid: false, validCount: 0});
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvContent = e.target?.result as string;
        const lines = csvContent.split('\n');

        if (lines.length === 0) {
          console.error('CSV is empty.');
          resolve({isValid: false, validCount: 0});
          return;
        }

        let validCount = 0;

        for (const line of lines) {
          const trimmedLine = line.trim();

          if (trimmedLine === '') {
            continue;
          }

          if (batchType === 'linkedin' && !this.isValidLinkedinUrl(trimmedLine)) {
            // Invalid LinkedIn URL but continue checking others
            continue;
          } else if (batchType === 'email' && !this.isValidEmail(trimmedLine)) {
            // Invalid email but continue checking others
            continue;
          }

          if (trimmedLine.includes(',')) {
            // CSV format issue but continue checking others
            continue;
          }

          // If we got here, the entry is valid
          validCount++;
        }

        // Return valid if we found at least one valid entry
        resolve({isValid: validCount > 0, validCount: validCount});
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        reject(error);
      };

      reader.readAsText(file);
    });
  }

  /**
   * Downloads sample template CSV based on batch type
   * @param batchType Type of data ('email' or 'linkedin')
   */
  downloadCSV(batchType: string = 'email'): void {
    // You can customize this to provide different templates based on batch type
    const fileUrl = batchType === 'email'
      ? 'assets/files/email_template.csv'
      : 'assets/files/linkedin_template.csv';

    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = batchType === 'email' ? 'email_template.csv' : 'linkedin_template.csv';
    a.click();
  }

  /**
   * Validates if a string is a LinkedIn URL
   * @param url String to validate
   * @returns boolean indicating if string is a valid LinkedIn URL
   */
  isValidLinkedinUrl(url: string): boolean {
    const urlRegex =
      /^(?:(?:http|https):\/\/)?(?:www\.)?linkedin\.com\/(?:in\/)?[a-zA-Z0-9\-]+(?:\.[a-zA-Z0-9\-]+)*\/?$/i;
    return urlRegex.test(url);
  }

  /**
   * Validates if a string is an email address
   * @param email String to validate
   * @returns boolean indicating if string is a valid email
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  /**
   * Uploads CSV file to the server
   * @param file File to upload
   * @param batchType Type of data ('email' or 'linkedin')
   * @returns Promise with the result of the upload
   */
  private uploadCSV(file: File, batchType: string = 'email'): Promise<any> {
    this.store.dispatch(uploadCSV({ file: file, batchType: batchType }));

    // Observe success and error streams
    return new Promise((resolve, reject) => {
      this.store
        .select(selectUploadCSV)
        .pipe(
          filter((result) => result !== null),
          take(1) // Automatically unsubscribe after one emission
        )
        .subscribe({
          next: (result) => {
            resolve(result);
          },
          error: (error) => {
            // Safely handle error objects that might not have the expected structure
            const errorMessage = error && error.error && error.error.debug && error.error.debug.message
              ? error.error.debug.message
              : 'An unexpected error occurred during CSV upload';
            reject({ message: errorMessage });
          },
        });

      this.store
        .select(selectUploadCSVFailure)
        .pipe(
          filter((error) => !!error),
          take(1) // Automatically unsubscribe after one emission
        )
        .subscribe({
          next: (error) => {
            // Safely handle error objects that might not have the expected structure
            const errorMessage = error && error.error && error.error.debug && error.error.debug.message
              ? error.error.debug.message
              : 'An unexpected error occurred during CSV upload';
            reject({ message: errorMessage });
          },
          error: (error) => {
            // Safely handle error objects that might not have the expected structure
            const errorMessage = error && error.error && error.error.debug && error.error.debug.message
              ? error.error.debug.message
              : 'An unexpected error occurred during CSV upload';
            reject({ message: errorMessage });
          },
        });
    });
  }

  /**
   * Uploads email addresses to the server
   * @param emails Array of email addresses
   * @returns Promise with the result of the upload
   */
  private uploadEmails(emails: string[]): Promise<any> {
    console.log('[FileUploadService] uploadEmails dispatching action with', emails.length, 'emails');
    this.store.dispatch(uploadEmails({ emails: emails }));

    // Observe success and error streams
    return new Promise((resolve, reject) => {
      // Set up a timeout to avoid hanging indefinitely
      const timeout = setTimeout(() => {
        console.warn('[FileUploadService] Email upload timeout after 30 seconds');
        reject({ message: 'Email upload timed out. Please try again.' });
      }, 30000);

      console.log('[FileUploadService] Waiting for upload emails response');
      this.store
        .select(selectUploadEmails)
        .pipe(
          filter((result) => result !== null),
          take(1) // Automatically unsubscribe after one emission
        )
        .subscribe({
          next: (result) => {
            clearTimeout(timeout);
            console.log('[FileUploadService] Upload emails success result:', result);
            resolve(result);
          },
          error: (error) => {
            clearTimeout(timeout);
            console.error('[FileUploadService] Upload emails error in success stream:', error);
            // Safely handle error objects that might not have the expected structure
            const errorMessage = error && error.error && error.error.debug && error.error.debug.message
              ? error.error.debug.message
              : 'An unexpected error occurred during email upload';
            reject({ message: errorMessage });
          },
        });

      this.store
        .select(selectUploadEmailsFailure)
        .pipe(
          filter((error) => !!error),
          take(1) // Automatically unsubscribe after one emission
        )
        .subscribe({
          next: (error) => {
            clearTimeout(timeout);
            console.error('[FileUploadService] Upload emails failure result:', error);
            // Safely handle error objects that might not have the expected structure
            const errorMessage = error && error.error && error.error.debug && error.error.debug.message
              ? error.error.debug.message
              : 'An unexpected error occurred during email upload';
            reject({ message: errorMessage });
          },
          error: (error) => {
            clearTimeout(timeout);
            console.error('[FileUploadService] Upload emails error in failure stream:', error);
            // Safely handle error objects that might not have the expected structure
            const errorMessage = error && error.error && error.error.debug && error.error.debug.message
              ? error.error.debug.message
              : 'An unexpected error occurred during email upload';
            reject({ message: errorMessage });
          },
        });
    });
  }

}
